
import React from 'react';
import { TipsManagement } from "@/components/admin/TipsManagement";
import { PrizesManagement } from "@/components/admin/PrizesManagement";
import { WinnersManagement } from "@/components/admin/winners/WinnersManagement";
import { UserRolesManagement } from "@/components/admin/UserRolesManagement";
import { DashboardAnalytics } from "@/components/admin/analytics/DashboardAnalytics";
import { UniversityManagement } from "@/components/admin/UniversityManagement";
import { PrizeAllocationsManagement } from "@/components/admin/PrizeAllocationsManagement";
import { ExportsManagement } from "@/components/admin/ExportsManagement";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const AdminDashboard = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    console.log('Setting up real-time subscriptions...');
    
    const channels = [
      supabase
        .channel('tips-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'tips' }, 
          (payload) => {
            console.log('Tips change received:', payload);
            queryClient.invalidateQueries({ queryKey: ['tips'] });
          }
        ),
      
      supabase
        .channel('prizes-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'prizes' }, 
          (payload) => {
            console.log('Prizes change received:', payload);
            queryClient.invalidateQueries({ queryKey: ['prizes'] });
          }
        ),
      
      supabase
        .channel('winners-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'winners' }, 
          (payload) => {
            console.log('Winners change received:', payload);
            queryClient.invalidateQueries({ queryKey: ['winners'] });
          }
        )
    ];

    Promise.all(channels.map(channel => channel.subscribe())).then(() => {
      console.log('All real-time subscriptions active');
    });

    return () => {
      console.log('Cleaning up real-time subscriptions...');
      channels.forEach(channel => {
        supabase.removeChannel(channel);
      });
    };
  }, [queryClient]);

  const { data: tips = [], error: tipsError, refetch: refetchTips } = useQuery({
    queryKey: ['tips'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tips')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    }
  });

  const { data: prizes = [], error: prizesError, refetch: refetchPrizes } = useQuery({
    queryKey: ['prizes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('prizes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    }
  });

  const { data: winners = [], error: winnersError, refetch: refetchWinners } = useQuery({
    queryKey: ['winners'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('winners')
        .select(`
          *,
          prizes (name),
          tips (content)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    }
  });

  if (tipsError || prizesError || winnersError) {
    toast({
      variant: "destructive",
      title: "Error",
      description: "Failed to fetch data",
    });
  }

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-8">Admin Panel</h1>
      <Tabs defaultValue="dashboard">
        <TabsList className="mb-8">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="tips">Tips</TabsTrigger>
          <TabsTrigger value="prizes">Prizes</TabsTrigger>
          <TabsTrigger value="winners">Winners</TabsTrigger>
          <TabsTrigger value="universities">Universities</TabsTrigger>
          <TabsTrigger value="allocations">Prize Allocations</TabsTrigger>
          <TabsTrigger value="exports">Exports</TabsTrigger>
        </TabsList>
        
        <TabsContent value="dashboard">
          <DashboardAnalytics />
        </TabsContent>
        
        <TabsContent value="users">
          <UserRolesManagement />
        </TabsContent>
        
        <TabsContent value="tips">
          <TipsManagement tips={tips} onRefresh={refetchTips} />
        </TabsContent>
        
        <TabsContent value="prizes">
          <PrizesManagement prizes={prizes} onRefresh={refetchPrizes} />
        </TabsContent>
        
        <TabsContent value="winners">
          <WinnersManagement winners={winners} onRefresh={refetchWinners} />
        </TabsContent>

        <TabsContent value="universities">
          <UniversityManagement />
        </TabsContent>

        <TabsContent value="allocations">
          <PrizeAllocationsManagement />
        </TabsContent>

        <TabsContent value="exports">
          <ExportsManagement />
        </TabsContent>
      </Tabs>
    </div>
  );
};
